/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Link } from 'react-router';

import { formatError } from '../atoms/Error';
import { Button } from '../ui/button';

import AddWebhookDialog from './AddWebhookDialog';

import DiscordLogo from '@/assets/discordLogo';
import SlackLogo from '@/assets/slackLogo';
import { OrganisationData } from '@/commons/commons';
import { toast } from '@/components/ui/use-toast';
import { useMutate, useGet } from '@/net/commons';

export default function NotificationSettings() {
    const { data, isLoading, error, mutate } = useGet<OrganisationData>(`/bff/organisation`);

    const { trigger: triggerRemove } = useMutate('/bff/organisation/webhook-url/:id', {
        revalidate: true,
        method: 'DELETE',
    });

    const removeWebhookUrl = async (type: string) => {
        try {
            const info = type === 'slackWebhookUrl' ? 'Slack' : 'Discord';
            await triggerRemove({ idParam: type });
            mutate();
            toast({
                title: `The ${info} webhook URL has been removed successfully.`,
            });
        } catch (err) {
            toast({
                title: `There was a problem with your request`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    if (error) {
        return <div>Error loading settings</div>;
    }

    if (isLoading) {
        return <div>Loading...</div>; // Show a loading state until data is fetched
    }
    return (
        <div className="flex flex-col mt-10">
            <div className="rounded-xl border p-6 space-y-8">
                <p className="text-sm w-3/4">
                    Notifications can be turned on for each suite in the suite preferences tab.
                </p>

                <div className="flex flex-col mt-6">
                    <h2 className="flex flex-row  items-center  gap-1 text-lg font-medium">
                        <div className="h-4 w-4">
                            <SlackLogo />
                        </div>
                        Slack
                        <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                            DEPRECATED
                        </span>
                    </h2>
                    <div className="mb-2 text-sm text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                        ⚠️ Slack webhook integration is being deprecated. Please use the Slack app integration available
                        in the{' '}
                        <Link to="/organisation/subscriptions" className="font-medium underline">
                            Subscriptions tab
                        </Link>{' '}
                        instead. The Slack app provides better notifications with richer features than simple webhooks.
                    </div>
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center md:max-w-5xl">
                        {data?.slackWebhookUrl ? (
                            <>
                                <p className="text-sm w-full md:w-3/4 truncate">
                                    Webhook URL {'('}
                                    <span className="underline text-blue-600">
                                        <Link to="https://heal.dev/docs/deployment/notifications/slack">docs</Link>
                                    </span>
                                    {')'} : {data?.slackWebhookUrl}{' '}
                                </p>
                                <div className="self-end mt-2 md:mt-0">
                                    <Button
                                        className="h-min w-min whitespace-nowrap mr-2"
                                        type="submit"
                                        onClick={() => removeWebhookUrl('slackWebhookUrl')}
                                        variant="destructive"
                                    >
                                        Remove
                                    </Button>
                                    <AddWebhookDialog
                                        type="slackWebhookUrl"
                                        mode="edit"
                                        onUpdate={() => {
                                            mutate();
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm w-full md:w-3/4 truncate">
                                    Webhook URL {'('}
                                    <span className="underline text-blue-600">
                                        <Link to="https://heal.dev/docs/deployment/notifications/slack">docs</Link>
                                    </span>
                                    {')'} : No webhook URL set
                                </p>
                                <AddWebhookDialog
                                    type="slackWebhookUrl"
                                    mode="add"
                                    onUpdate={() => {
                                        mutate();
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col mt-6">
                    <h2 className="flex flex-row  items-center gap-1 text-lg font-medium">
                        <div className="h-4 w-4 pt-0.5">
                            <DiscordLogo />
                        </div>
                        Discord
                    </h2>
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center md:max-w-5xl">
                        {data?.discordWebhookUrl ? (
                            <>
                                <p className="text-sm w-full md:w-3/4 truncate">
                                    Webhook URL {'('}
                                    <span className="underline text-blue-600">
                                        <Link to="https://heal.dev/docs/deployment/notifications/slack">docs</Link>
                                    </span>
                                    {')'} : {data?.discordWebhookUrl}{' '}
                                </p>
                                <div className="self-end mt-2 md:mt-0">
                                    <Button
                                        className="h-min w-min whitespace-nowrap mr-2"
                                        type="submit"
                                        onClick={() => removeWebhookUrl('discordWebhookUrl')}
                                        variant="destructive"
                                    >
                                        Remove
                                    </Button>
                                    <AddWebhookDialog
                                        type="discordWebhookUrl"
                                        mode="edit"
                                        onUpdate={() => {
                                            mutate();
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm w-full md:w-3/4 truncate">
                                    Webhook URL {'('}
                                    <span className="underline text-blue-600">
                                        <Link to="https://heal.dev/docs/deployment/notifications/discord">docs</Link>
                                    </span>
                                    {')'} : No webhook URL set
                                </p>
                                <AddWebhookDialog
                                    type="discordWebhookUrl"
                                    mode="add"
                                    onUpdate={() => {
                                        mutate();
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
